<template lang="pug">
.orders-history.rogue-scroll-y(:class="{'centered': loading}")
  NativeAppPlaceholder(
    v-if="!loading && !fetching && (!loggedIn || !allOrders.length)"
    :media-config="{ src: '/Rogue%20App%20Dashboard/Orders/delivery-truck-gray_zp5fl7.svg', width: 98, height: 60 }"
  )
    p.placeholder-text(v-if="loggedIn") You have not placed any orders
    p.placeholder-text(v-else) Create a Rogue Account to see a step-by-step status of your orders and historical order data.
    RogueButton(v-if="!loggedIn" width="full" size="m" color="black" @click="login") Login or Create An Account
  NativeAppSpinnerWrapper(v-else :active="loading && !fetching")
    .order-list-wrapper
      OrdersListItem(
        v-if="!loading && allOrders.length"
        v-for="order in allOrders"
        :key="order._id"
        :order="order"
      )
      RogueButton.load-more(
        v-if="!loading && allOrders.length < totalOrders"
        id="btn-load-more"
        width="full"
        :loading="fetching"
        @click="loadMore"
        text="Load More"
      )
</template>

<script setup lang="ts">
import { messages } from 'Src/js/app-bridge/messages'
import { fetchOrders } from 'Src/js/helpers/fetchOrders'
import TrackOrderForm from 'Src/js/pages/rss/components/track-order-form.vue'
import { computed, onMounted, ref, watch } from 'vue'
import type { IOrder } from '@rogue/universal'
import NativeAppPlaceholder from './native-app-placeholder.vue'
import NativeAppSpinnerWrapper from './native-app-spinner-wrapper.vue'
import OrdersListItem from './native-app-orders-list-item.vue'
import TrackOrders from './native-app-track-orders.vue'
import { useUIStore } from '@/js/stores/uiStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useRogue } from '@/js/services/Rogue'

type OrderListResponse = {
  orders: IOrder[];
  count: number;
}

const customerStore = useCustomerStore()
const uiStore = useUIStore()
const rogue = useRogue()

const loading = ref<boolean>(true)
const fetching = ref<boolean>(false)
const allOrders = ref<IOrder[]>([])
const orders = ref<IOrder[]>([])
const totalOrders = ref<number>(0)
const currentPage = ref<number>(1)

const loggedIn = computed<boolean>(() => customerStore.loggedIn)
const lastContentReloadAt = computed<number>(() => uiStore.appLastContentReloadAt || Date.now())

function refreshData() {
  uiStore.appLastContentReloadAt = Date.now()
}

async function load() {
  if (!loggedIn.value) {
    return
  }

  loading.value = !fetching.value
  try {
    const resp: OrderListResponse = await fetchOrders(rogue, 20, currentPage.value)
    orders.value = resp.orders
    totalOrders.value = resp.count || 0
  } catch (e) {
    rogue.log.error('Native App Orders List: Unable to load orders', e)
  }
  loading.value = false
}

function login() {
  loading.value = true
  rogue.emitToApp(messages.ORDERS_LOGIN)
}

async function loadMore() {
  fetching.value = true
  currentPage.value += 1
  await load()
  fetching.value = false
}

function reset() {
  allOrders.value = []
  orders.value = []
  totalOrders.value = 0
  currentPage.value = 1
}

watch(lastContentReloadAt, async (curr, prev) => {
  if (curr > prev) {
    reset()
    await load()
    loading.value = false
  }
})

watch(orders, (list) => {
  if (list) {
    list.forEach(order => {
      // Prevents to duplicate orders in the list
      // @ts-expect-error _id is present but not defined on type
      if (allOrders.value.find(o => o._id === order._id)) {
        return
      }

      allOrders.value.push(order)
    })
  }
})

watch(loggedIn, (isLoggedIn) => {
  if (isLoggedIn) {
    refreshData()
  } else {
    reset()
  }
})

onMounted(async () => {
  await customerStore.init(rogue)
  // Initialize the value and trigger `load`
  refreshData()
})
</script>

<style scoped lang="scss">
// Scroll area height
$scrollHeight: calc(100vh - 49px);

.orders-history {
  display: flex;
  flex-direction: column;
  align-items: center;

  &.centered {
    justify-content: center;
  }
}
.orders-history, .placeholder {
  height: $scrollHeight;
  width: 100%;
}
.placeholder:deep(.placeholder-icon) {
  height: 60px;
  width: 98px;

  img {
    max-height: 60px;
    transform: translateY(4px);
  }
}
.spinner-container.centered {
  height: $scrollHeight;
}
.load-more {
  border-radius: 0;
}

.order-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: $space-xxxs;
  padding: $space-s;
}
</style>
